<template>
  <div v-title="$route.meta.title"
       class="training-instruction-form">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/training/instruction' }">培训管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ pageTitle }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="training-instruction-form__card">
      <div class="training-instruction-form__card-header"
           @click="formExpanded = !formExpanded">
        <span>表单</span>
        <i :class="formExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
      </div>

      <el-form v-show="formExpanded"
               ref="form"
               v-loading="detailLoading"
               :model="form"
               :rules="formRules"
               size="medium"
               label-width="0"
               class="training-instruction-form__body"
               @submit.native.prevent="onSubmit()">
        <section class="training-instruction-form__section">
          <h3 class="training-instruction-form__section-title">基本信息</h3>

          <div class="training-instruction-form__row">
            <label class="training-instruction-form__label is-required">标题</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="title"
                            class="training-instruction-form__form-item">
                <el-input v-model="form.title"
                          placeholder="请输入培训标题"
                          maxlength="255"
                          show-word-limit
                          clearable />
              </el-form-item>
            </div>
          </div>

          <div class="training-instruction-form__row">
            <label class="training-instruction-form__label is-required">封面</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="cover_imgs"
                            class="training-instruction-form__form-item">
                <el-upload ref="coverUpload"
                           class="training-instruction-form__cover-uploader"
                           action=""
                           drag
                           :auto-upload="false"
                           :show-file-list="false"
                           accept="image/jpeg,image/png,image/jpg"
                           :disabled="coverUploading"
                           :on-change="onCoverChange">
                  <div v-if="coverPreviewUrl"
                       class="training-instruction-form__cover-preview">
                    <img :src="coverPreviewUrl"
                         alt="">
                    <div class="training-instruction-form__cover-actions">
                      <el-button type="text"
                                 @click.stop="removeCover">移除</el-button>
                    </div>
                  </div>
                  <div v-else
                       class="training-instruction-form__cover-placeholder">
                    <i class="el-icon-upload" />
                    <div class="training-instruction-form__cover-placeholder-text">点击上传或拖拽图片到此处</div>
                    <div class="training-instruction-form__cover-placeholder-tip">支持 JPG、PNG，建议尺寸 686×292</div>
                  </div>
                </el-upload>
              </el-form-item>
            </div>
          </div>
        </section>

        <section class="training-instruction-form__section">
          <h3 class="training-instruction-form__section-title">受众与展示</h3>
          <template v-if="isWorkerUser">
            <div class="training-instruction-form__row">
              <label class="training-instruction-form__label is-required">是否在App展示</label>
              <div class="training-instruction-form__field">
                <el-form-item prop="display_in_app_learning_center"
                              class="training-instruction-form__form-item">
                  <el-radio-group v-model="form.display_in_app_learning_center"
                                  @change="onDisplayInAppChanged">
                    <el-radio v-for="it in opts.boolMaps"
                              :key="it.value"
                              :label="Number(it.value)">{{ it.label }}</el-radio>
                  </el-radio-group>
                </el-form-item>
              </div>
            </div>

            <div class="training-instruction-form__row">
              <label class="training-instruction-form__label is-required">优先级</label>
              <div class="training-instruction-form__field">
                <el-form-item prop="priority"
                              class="training-instruction-form__form-item">
                  <el-input-number v-model="form.priority"
                                   :min="0"
                                   :precision="0"
                                   controls-position="right"
                                   class="training-instruction-form__number-input" />
                  <div class="field-tip">在 App 学习中心内的展示顺序，数字越小优先级越高</div>
                </el-form-item>
              </div>
            </div>

            <div v-if="showLearningCenterGroup"
                 class="training-instruction-form__row">
              <label class="training-instruction-form__label">学习中心内所属分组</label>
              <div class="training-instruction-form__field">
                <el-form-item prop="instruction_group_name"
                              class="training-instruction-form__form-item">
                  <el-select v-model="form.instruction_group_name"
                             class="width-max"
                             filterable
                             allow-create
                             clearable
                             popper-class="training-instruction-group-select"
                             placeholder="请选择或者新建分组">
                    <el-option v-for="item in instructionGroups"
                               :key="item.value"
                               :label="item.label"
                               :value="item.label">
                      <span class="instruction-group-option">
                        <span class="instruction-group-option__label">{{ item.label }} / {{ item.value }}</span>
                        <el-button type="text"
                                   icon="el-icon-delete"
                                   class="instruction-group-option__delete"
                                   @click.stop="deleteInstructionGroup(item)" />
                      </span>
                    </el-option>
                  </el-select>
                  <div class="field-tip">仅用在 App 学习中心内筛选</div>
                </el-form-item>
              </div>
            </div>

            <div v-if="needsExam"
                 class="training-instruction-form__row">
              <label class="training-instruction-form__label is-required">重考间隔（天）</label>
              <div class="training-instruction-form__field">
                <el-form-item prop="retraining_days"
                              class="training-instruction-form__form-item">
                  <div class="training-instruction-form__inline-number">
                    <el-input-number v-model="form.retraining_days"
                                     :min="0"
                                     :precision="0"
                                     controls-position="right"
                                     class="training-instruction-form__number-input" />
                    <span class="training-instruction-form__inline-suffix">天</span>
                  </div>
                  <div class="field-tip">0 表示培训一次永久有效</div>
                </el-form-item>
              </div>
            </div>
          </template>
        </section>

        <section class="training-instruction-form__section">
          <h3 class="training-instruction-form__section-title">内容配置</h3>

          <el-alert v-if="isContentConfigLocked"
                    class="content-lock-alert"
                    type="info"
                    :closable="false"
                    show-icon
                    title="培训非新创建状态，所属用户类型、是否通用、所属客户、所属知识库、类型、关联教程、关联考试不可修改" />

          <div class="training-instruction-form__row">
            <label class="training-instruction-form__label is-required">是否通用</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="is_common"
                            class="training-instruction-form__form-item">
                <el-radio-group v-model="form.is_common"
                                :disabled="isContentConfigLocked"
                                @change="onIsCommonChanged">
                  <el-radio v-for="it in opts.isCommonMaps"
                            :key="it.value"
                            :label="it.value">{{ it.label }}</el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </div>

          <div v-if="form.is_common === consts.IS_COMMON_DEDICATED"
               class="training-instruction-form__row">
            <label class="training-instruction-form__label is-required">所属客户</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="customerid"
                            class="training-instruction-form__form-item">
                <remote-select class="width-max"
                               clearable
                               placeholder="请输入客户名称/编号搜索"
                               v-model="form.customerid"
                               :disabled="isContentConfigLocked"
                               :query-request="getBasicCustomerTable"
                               :find-request="getBasicCustomerDetail"
                               @change="onContentConfigChanged" />
              </el-form-item>
            </div>
          </div>

          <div class="training-instruction-form__row">
            <label class="training-instruction-form__label is-required">所属知识库</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="training_knowledge_id"
                            class="training-instruction-form__form-item">
                <div class="training-instruction-form__field-with-action">
                  <remote-select class="width-max"
                                 clearable
                                 keyword-key="name"
                                 label-key="name"
                                 placeholder="请输入知识库名称搜索"
                                 v-model="form.training_knowledge_id"
                                 :disabled="isContentConfigLocked || (form.is_common === consts.IS_COMMON_DEDICATED && !form.customerid)"
                                 :query-request="queryTrainingKnowledge"
                                 :find-request="getTrainingKnowledge"
                                 :query-params="knowledgeQueryParams" />
                  <el-tooltip v-if="!isContentConfigLocked"
                              content="新建知识库"
                              placement="top">
                    <el-button type="text"
                               icon="el-icon-circle-plus-outline"
                               class="training-instruction-form__field-action"
                               @click="openKnowledgeDialog" />
                  </el-tooltip>
                </div>
              </el-form-item>
            </div>
          </div>

          <div class="training-instruction-form__row">
            <label class="training-instruction-form__label is-required">类型</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="instruction_type"
                            class="training-instruction-form__form-item">
                <el-radio-group v-model="form.instruction_type"
                                :disabled="isContentConfigLocked"
                                @change="onInstructionTypeChanged">
                  <el-radio v-for="it in opts.typeMaps"
                            :key="it.value"
                            :label="Number(it.value)">{{ it.label }}</el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </div>

          <div v-if="needsCourse"
               class="training-instruction-form__row">
            <label :class="['training-instruction-form__label', { 'is-required': needsCourse }]">关联教程</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="training_course_id"
                            class="training-instruction-form__form-item">
                <div class="training-instruction-form__field-with-action">
                  <remote-select class="width-max"
                                 clearable
                                 keyword-key="title"
                                 label-key="title"
                                 placeholder="请输入教程标题搜索"
                                 v-model="form.training_course_id"
                                 :disabled="isContentConfigLocked"
                                 :query-request="queryUserTrainingCourse"
                                 :query-params="{...relatedContentQueryParams, status: courseConsts.STATUS_ONLINE}"
                                 :find-request="getTrainingCourse" />
                  <el-tooltip v-if="!isContentConfigLocked"
                              content="新建教程"
                              placement="top">
                    <el-button type="text"
                               icon="el-icon-circle-plus-outline"
                               class="training-instruction-form__field-action"
                               @click="goCreateCourse" />
                  </el-tooltip>
                </div>
              </el-form-item>
            </div>
          </div>

          <div v-if="needsExam"
               class="training-instruction-form__row">
            <label :class="['training-instruction-form__label', { 'is-required': needsExam }]">关联考试</label>
            <div class="training-instruction-form__field">
              <el-form-item prop="training_examination_id"
                            class="training-instruction-form__form-item">
                <div class="training-instruction-form__field-with-action">
                  <remote-select class="width-max"
                                 clearable
                                 keyword-key="title"
                                 label-key="title"
                                 placeholder="请输入考试标题搜索"
                                 v-model="form.training_examination_id"
                                 :disabled="isContentConfigLocked"
                                 :query-request="queryTrainingExamination"
                                 :query-params="{...relatedContentQueryParams, status: consts.EXAMINATION_STATUS_ONLINE}"
                                 :find-request="getTrainingExamination" />
                  <el-tooltip v-if="!isContentConfigLocked"
                              content="新建考试"
                              placement="top">
                    <el-button type="text"
                               icon="el-icon-circle-plus-outline"
                               class="training-instruction-form__field-action"
                               @click="goCreateExamination" />
                  </el-tooltip>
                </div>
              </el-form-item>
            </div>
          </div>
        </section>

        <div class="training-instruction-form__footer">
          <el-button @click="$router.back()">取消</el-button>
          <el-button type="primary"
                     native-type="submit"
                     :loading="submitting">{{ submitButtonText }}</el-button>
        </div>
      </el-form>
    </div>

    <training-knowledge-form-dialog ref="knowledgeDialog"
                                    @success="onKnowledgeCreated" />
  </div>
</template>

<script>
import { createFormRules, createRelatedRules } from '../../training-instruction.rules'
import { opts, consts } from '@views/training/training-instruction.constants'
import { consts as knowledgeConsts } from '@views/training/training-knowledge.constants'
import { consts as courseConsts } from '@views/training/training-course.constants'
import trainingMixins from '@views/training/training.mixins'
import TrainingKnowledgeFormDialog from '@views/training/components/TrainingKnowledgeFormDialog.vue'

export default {
  components: {
    TrainingKnowledgeFormDialog,
  },
  mixins: [trainingMixins],
  data () {
    return {
      id: 0,
      formExpanded: true,
      submitting: false,
      detailLoading: false,
      coverUploading: false,
      opts,
      consts,
      courseConsts,
      form: {
        title: '',
        cover_imgs: [],
        target_usertype: 1,
        display_in_app_learning_center: 1,
        priority: 0,
        instruction_group_name: null,
        retraining_days: 0,
        is_common: 0,
        customerid: null,
        training_knowledge_id: null,
        instruction_type: null,
        training_course_id: null,
        training_examination_id: null,
        instruction_status: consts.STATUS_NEW,
      },
    }
  },
  computed: {
    pageTitle () {
      return this.id ? '编辑培训' : '创建培训'
    },
    submitButtonText () {
      return this.id ? '保存修改' : '创建培训'
    },
    formRules () {
      return Object.assign(
        {},
        createFormRules(this.form),
        createRelatedRules(this.needsCourse, this.needsExam)
      )
    },
    coverPreviewUrl () {
      const cover = this.form.cover_imgs[0]
      return cover && cover.url ? cover.url : ''
    },
    isContentConfigLocked () {
      return this.id > 0 && Number(this.form.instruction_status) !== consts.STATUS_NEW
    },
    isWorkerUser () {
      return Number(this.form.target_usertype) === consts.TARGETUSER_TYPE_USER
    },
    showLearningCenterGroup () {
      return this.isWorkerUser && Number(this.form.display_in_app_learning_center) === consts.TRUE
    },
    needsCourse () {
      return [consts.TYPE_ONLY_LEARNING, consts.TYPE_LEARNING_AND_EXAM].includes(Number(this.form.instruction_type))
    },
    needsExam () {
      return [consts.TYPE_ONLY_EXAM, consts.TYPE_LEARNING_AND_EXAM].includes(Number(this.form.instruction_type))
    },
    knowledgeQueryParams () {
      const params = {
        status: knowledgeConsts.STATUS_ACTIVE,
        is_common: this.form.is_common,
      }
      if (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && this.form.customerid) {
        params.customerid = this.form.customerid
      }
      return params
    },
    relatedContentQueryParams () {
      const params = {
        target_usertype: this.form.target_usertype,
        is_common: this.form.is_common,
      }
      if (this.form.training_knowledge_id) {
        params.training_knowledge_id = this.form.training_knowledge_id
      }
      if (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && this.form.customerid) {
        params.customerid = this.form.customerid
      }
      return params
    },
  },
  created () {
    this.onLoad()
  },
  methods: {
    ...request.map([
      'getBasicCustomerTable',
      'getBasicCustomerDetail',
      'queryTrainingKnowledge',
      'getTrainingKnowledge',
      'queryUserTrainingCourse',
      'getTrainingCourse',
      'queryTrainingExamination',
      'getTrainingExamination',
      'getTrainingSignedOssUrl',
    ]),
    onLoad () {
      if (this.$route.params.id) {
        this.id = Number(this.$route.params.id)
        this.loadDetail(this.id)
      }
      this.loadGroups()
    },
    openRouteInNewTab (route) {
      const routeData = this.$router.resolve(route)
      window.open(routeData.href, '_blank')
    },
    openKnowledgeDialog () {
      if (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && !this.form.customerid) {
        this.$message.warning('请先选择所属客户')
        return
      }
      this.$refs.knowledgeDialog.openCreate({
        is_common: this.form.is_common,
        customerid: this.form.customerid,
      })
    },
    onKnowledgeCreated (payload) {
      if (payload && payload.id) {
        this.form.training_knowledge_id = Number(payload.id)
        this.$refs.form && this.$refs.form.validateField('training_knowledge_id')
      }
    },
    goCreateCourse () {
      this.openRouteInNewTab({ path: '/training/user-course/create' })
    },
    goCreateExamination () {
      this.openRouteInNewTab({ path: '/training/examination/create' })
    },
    loadDetail (id) {
      this.detailLoading = true
      request.common('getTrainingInstruction', { id }).then(({ data }) => {
        filter.setLoop(this.form, data)
        this.form.is_common = Number(this.form.is_common)
        this.form.cover_imgs = Array.isArray(data.cover_imgs) ? data.cover_imgs : []
        this.form.customerid = data.customerid > 0 ? String(data.customerid) : null
        this.form.training_course_id = data.training_course_id > 0 ? data.training_course_id : null
        this.form.training_examination_id = data.training_examination_id > 0 ? data.training_examination_id : null
      }).catch(() => {
        this.$message.error('培训详情加载失败')
      }).finally(() => {
        this.detailLoading = false
      })
    },
    onTargetUsertypeChanged (value) {
      if (Number(value) === consts.TARGETUSER_TYPE_INTERN) {
        this.form.display_in_app_learning_center = consts.FALSE
        this.form.instruction_group_name = null
      }
      this.onContentConfigChanged()
    },
    onDisplayInAppChanged (value) {
      if (Number(value) !== consts.TRUE) {
        this.form.instruction_group_name = null
      }
    },
    onIsCommonChanged (value) {
      if (Number(value) === 1) {
        this.form.customerid = null
      }
      this.onContentConfigChanged()
    },
    onInstructionTypeChanged () {
      if (!this.needsCourse) {
        this.form.training_course_id = null
      }
      if (!this.needsExam) {
        this.form.training_examination_id = null
      }
    },
    onContentConfigChanged () {
      this.form.training_knowledge_id = null
      this.form.training_course_id = null
      this.form.training_examination_id = null
      if (!this.isWorkerUser) {
        this.form.instruction_group_name = null
      }
    },
    onCoverChange (file) {
      const raw = file.raw
      if (!raw) {
        return
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']
      if (!allowedTypes.includes(raw.type)) {
        this.$message.error('请上传 jpg/png 图片')
        this.$refs.coverUpload && this.$refs.coverUpload.clearFiles()
        return
      }
      if (raw.size / 1024 / 1024 > 20) {
        this.$message.error('图片大小不能超过 20MB')
        this.$refs.coverUpload && this.$refs.coverUpload.clearFiles()
        return
      }
      this.uploadCover(raw)
    },
    async uploadCover (file) {
      this.coverUploading = true
      try {
        const ext = file.name.split('.').pop().toLowerCase()
        const { data } = await request.common('getTrainingSignedOssUrl', {
          file_name: file.name,
          ext,
          content_type: file.type,
        })
        const uploadRes = await fetch(data.signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': data.content_type },
          body: file,
        })
        if (!uploadRes.ok) {
          this.$message.error(`上传失败: ${uploadRes.status}`)
          return
        }
        this.form.cover_imgs = [{
          name: file.name,
          url: data.url,
          uid: Date.now(),
          size: file.size,
        }]
        this.$refs.form && this.$refs.form.validateField('cover_imgs')
        this.$message.success('封面上传成功')
      } catch (error) {
        this.$message.error(error.message || '封面上传失败')
        this.$refs.coverUpload && this.$refs.coverUpload.clearFiles()
      } finally {
        this.coverUploading = false
      }
    },
    removeCover () {
      this.form.cover_imgs = []
      this.$refs.coverUpload && this.$refs.coverUpload.clearFiles()
      this.$refs.form && this.$refs.form.validateField('cover_imgs')
    },
    getSubmitPayload () {
      const payload = filter.copyJson(this.form)
      if (Number(payload.is_common) === 1) {
        payload.customerid = 0
      }
      if (!this.needsCourse) {
        payload.training_course_id = 0
      }
      if (!this.needsExam) {
        payload.training_examination_id = 0
      }
      delete payload.instruction_status
      return payload
    },
    onSubmit () {
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.$message.error('请检查表单填写是否正确')
          return
        }
        this.submitting = true
        const requestName = this.id ? 'updateTrainingInstruction' : 'createTrainingInstruction'
        const params = this.id
          ? { id: this.id, ...this.getSubmitPayload() }
          : this.getSubmitPayload()
        request.common(requestName, params).then(() => {
          this.$message.success(this.id ? '保存成功' : '创建成功')
          this.$router.push({ path: '/training/instruction' })
        }).finally(() => {
          this.submitting = false
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.training-instruction-form {
  &__card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
    user-select: none;
    color: #606266;
    font-size: 14px;
    font-weight: 500;

    i {
      color: #909399;
    }
  }

  &__body {
    padding: 20px 24px 0;
  }

  &__section + &__section {
    margin-top: 24px;
  }

  &__section-title {
    margin: 0 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
  }

  &__row {
    display: grid;
    grid-template-columns: minmax(120px, 16.666%) minmax(0, 1fr);
    gap: 16px;
    align-items: start;
    margin-bottom: 16px;
  }

  &__label {
    padding-top: 8px;
    color: #606266;
    font-size: 14px;
    line-height: 1.5;

    &.is-required::before {
      content: '*';
      color: #f56c6c;
      margin-right: 4px;
    }
  }

  &__field {
    min-width: 0;
  }

  &__field-with-action {
    display: flex;
    align-items: flex-start;
    gap: 8px;

    .width-max {
      flex: 1;
      min-width: 0;
    }
  }

  &__field-action {
    flex-shrink: 0;
    padding: 4px;
    margin-top: 2px;
    font-size: 18px;
    color: #409eff;
    line-height: 1;

    &:hover,
    &:focus {
      color: #66b1ff;
    }
  }

  &__form-item {
    margin-bottom: 0;
  }

  &__number-input {
    width: 160px;
  }

  &__inline-number {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__inline-suffix {
    color: #909399;
    font-size: 14px;
  }

  &__cover-uploader {
    width: 100%;

    ::v-deep .el-upload {
      width: 100%;
    }

    ::v-deep .el-upload-dragger {
      width: 100%;
      height: auto;
      min-height: 160px;
      padding: 0;
      border: 2px dashed #dcdfe6;
      border-radius: 8px;
      background: #fafafa;
      transition: border-color 0.2s ease;

      &:hover {
        border-color: #409eff;
      }
    }
  }

  &__cover-placeholder {
    padding: 36px 24px;
    text-align: center;

    .el-icon-upload {
      font-size: 32px;
      color: #c0c4cc;
      margin-bottom: 8px;
    }
  }

  &__cover-placeholder-text {
    color: #909399;
    font-size: 14px;
    line-height: 1.6;
  }

  &__cover-placeholder-tip {
    margin-top: 4px;
    color: #c0c4cc;
    font-size: 12px;
    line-height: 1.5;
  }

  &__cover-preview {
    position: relative;
    width: 100%;
    max-width: 686px;
    aspect-ratio: 686 / 292;
    background: #f5f7fa;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__cover-actions {
    position: absolute;
    right: 8px;
    bottom: 8px;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 4px;
    padding: 0 4px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin: 8px -24px 0;
    padding: 12px 24px;
    background: #f5f7fa;
    border-top: 1px solid #ebeef5;
    border-radius: 0 0 8px 8px;
  }
}

.field-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.content-lock-alert {
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .training-instruction-form {
    &__row {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    &__label {
      padding-top: 0;
    }
  }
}
</style>

<style lang="scss">
.training-instruction-form__cover-uploader .el-upload-dragger.is-dragover {
  border-color: #409eff;
}

.training-instruction-group-select {
  .instruction-group-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
  }

  .instruction-group-option__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .instruction-group-option__delete {
    flex-shrink: 0;
    padding: 0 4px;
    color: #f56c6c;

    &:hover,
    &:focus {
      color: #f78989;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/instruction/TrainingInstructionForm.vue